import React from 'react';

const DEFAULT_REPLACEMENTS = {
    break: <br />,
    "\n": <br />,
};

/**
 * Internationalization service
 *
 * Uses a dynamic import to pick common defaults, which are ovridden by user settings at runtime
 */
class I18n {
    // Pool of translation entries
    pool = null

    // Pool of setting key translations
    settingsPool = null

    // Pool of user overrides
    userOverrides = {}

    // What to show if the key is missing
    missingText = '__I18N_ERROR__'

    // Delimiter for replaceable elements
    replacementDelim = '%'

    async init({ locale }) {
        const { default: data } = await import(`../locales/${locale.default}.js`);
        if (locale.default in locale) {
            // Pick any overrides that exist for the current locale
            this.setOverrides(locale[locale.default]);
        }

        return this.setData(data);
    }

    async initSettings({ locale }) {
        const { default: data } = await import(`../locales/settings-${locale.default}.js`);

        return this.setSettingsData(data);
    }

    setData(data) {
        this.pool = this.parseTranslations(data);

        return this;
    }

    setSettingsData(data) {
        this.settingsPool = this.parseTranslations(data);

        return this;
    }

    setOverrides(overrides) {
        this.userOverrides = { ...overrides };
    }

    parseTranslations(data) {
        return data;
    }

    /**
     * Translate the given key, and make the provided replacements, using the currently active locale.
     * Supports primitives as well as React elements in the replacements
     *
     * @param {string} key from the translation file to use
     * @param {object} replacements named map which can contain anything, including react elements
     * @param {boolean} inSettings if true, search for the key in the settings translations
     */
    t(key, replacements = {}, inSettings = false) {
        // Overwrite default replacements
        replacements = { ...DEFAULT_REPLACEMENTS, ...replacements };

        if (!this.pool) {
            throw new Error('No translation pool available, did you forget to call, and wait for init() to complete?');
        }

        if (process.env.REACT_APP_BUILD_MODE === 'editor' && !this.settingsPool) {
            throw new Error('No translation pool available for SETTINGS, did you forget to call, and wait for initSettings() to complete?');
        }

        const hasBaseKey = (inSettings && (key in this.settingsPool)) || (!inSettings && (key in this.pool));

        // Even in case of overrides, a base key must exist
        if (!hasBaseKey) {
            // Don't halt runtime, but show the text as error text, with a log
            console.error(`No translation available for key '${key}' ${inSettings ? 'in settings' : ''}!`);
            return this.missingText;
        }

        // Pick override, if present, else from the pool
        let result = [inSettings ? this.settingsPool[key] : (key in this.userOverrides) ? this.userOverrides[key] : this.pool[key]];
        if (replacements) {
            for (const [rKey, rValue] of Object.entries(replacements)) {
                // Test string is %KEY% by default
                let test;
                if (rKey === "\n") {
                    test = rKey;
                } else {
                    test = `${this.replacementDelim}${rKey}${this.replacementDelim}`;
                }
                // Keep reducing the same array of strings, breaking down each replacement in each iteration
                result = result.reduce((acc, curr) => {
                    if (typeof curr !== 'string' || curr.indexOf(test) < 0) {
                        acc.push(curr);
                    } else {
                        // Split by the key
                        const parts = curr.split(test);
                        parts.forEach((part, index) => {
                            acc.push(part);
                            // Suffix the replacement value after each, except last, part
                            if (index < (parts.length - 1)) {
                                acc.push(rValue);
                            }
                        });
                    }

                    return acc;
                }, []);
            }
        }

        return result;
    }

    /**
     * Wrapper method for translating settings keys
     *
     * @param {string} key from the translation file to use
     * @param {object} replacements named map which can contain anything, including react elements
     */
    ts(key, replacements) {
        return t(key, replacements, true);
    }

    /**
     * Wrapper method for translating keys with replacements, and wrapping the result in a span
     *
     * @param {string} key from the translation file to use
     * @param {object} replacements named map which can contain anything, including react elements
     */
    tspan(key, replacements, inSettings) {
        const result = this.t(key, replacements, inSettings);
        // The return is an array, so using it as single element is made easier by wrapping every element in a span with a unique-ish key (by default. this can be disabled)
        return result.map((part, index) => <span settings-key={key} key={`t-${key}-part-${index}`} className="i18n-done">{part}</span>);
    }
}

const translator = new I18n();
export default translator;
export const t = translator.t.bind(translator);
export const ts = translator.ts.bind(translator);
export const tspan = translator.tspan.bind(translator);
